import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GetCustomerSettingBySettingName } from 'services/ConfigurationService';
import DialogContentText from '@mui/material/DialogContentText';
import { MessageTemplate } from 'common/models/Templates/MessageTemplate';
import MessageTemplatePicker from 'components/Pickers/MessageTemplatePicker';
import ActionsSplitButton from 'components/SummaryBars/Actions/ActionsSplitButton';
import MenuItem from '@mui/material/MenuItem';
import { SendRejectionEmail, SendRejectionEmailByCandidateList } from 'services/CandidatesService';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { GetMessageTemplate } from 'services/TemplatesService';
import TextAreaWithFormat from 'components/TextAreaWithFormat';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import { CandidatesRejectionParams } from 'common/models/Jobs/CandidatesRejectionParams';

interface Props {
  open: boolean;
  jobId: number;
  candidateIds: number[];
  defaultRejectionTemplateId?: number;
  showUpdateRatingField?: boolean;
  closeHandler: () => void;
  onRejectionEmailSent: (isContinue?: boolean, shouldUpdateRating?: boolean) => void;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

const noTemplateOption: MessageTemplate = {
  id: 0,
  recruiterID: 0,
  statusID: 1,
  type: 0,
  typeName: '',
  name: 'Do not Send Rejection Email',
  subject: '',
  body: '',
  createdDate: '',
  deleted: false,
  contact: false,
  candidate: false,
  userFullName: null,
};
const specialOptions: MessageTemplate[] = [noTemplateOption];

export default function RejectionEmailDialog({
  open,
  jobId,
  candidateIds,
  defaultRejectionTemplateId = 0,
  showUpdateRatingField = false,
  closeHandler,
  onRejectionEmailSent,
  loadingHandler,
  errorHandler,
}: Props) {
  const [isMandatory, setIsMandatory] = useState(false);
  const [canEditCompanyTemplates, setCanEditCompanyTemplates] = useState(false);
  const [shouldUpdateRating, setShouldUpdateRating] = useState(false);
  const [rejectViaId, setRejectViaId] = useState(1);
  const [defaultTemplate, setDefaultTemplate] = useState<MessageTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');
  const [rejectionNotes, setRejectionNotes] = useState('');

  useEffect(() => {
    const getDefaultTemplate = async () => {
      if (defaultRejectionTemplateId > 0) {
        const res = await GetMessageTemplate(defaultRejectionTemplateId);
        if (res) setDefaultTemplate(res);
      }
    };
    getDefaultTemplate();
  }, [defaultRejectionTemplateId]);

  useEffect(() => {
    const getSettings = async () => {
      const [isMandatorySetting, canEditCompanyTemplatesSetting] = await Promise.all([
        GetCustomerSettingBySettingName('MandatoryRejectionEmails'),
        GetCustomerSettingBySettingName('RejectionEmailsEditableCompanyTemplates'),
      ]);
      if (isMandatorySetting) setIsMandatory(isMandatorySetting.toLowerCase() === 'true');
      if (canEditCompanyTemplatesSetting)
        setCanEditCompanyTemplates(canEditCompanyTemplatesSetting.toLowerCase() === 'true');
    };
    getSettings();
  }, []);

  useEffect(() => {
    if (!open) {
      setShouldUpdateRating(false);
      setRejectViaId(1);
      setRejectionNotes('');
      setTemplateSubject(defaultTemplate?.subject ?? '');
      setTemplateMessage(defaultTemplate?.body ?? '');
      setSelectedTemplate(defaultTemplate);
    }
  }, [open, defaultTemplate]);

  useEffect(() => {
    if (selectedTemplate) {
      setTemplateSubject(selectedTemplate.subject);
      setTemplateMessage(selectedTemplate.body);
    } else {
      setTemplateSubject('');
      setTemplateMessage('');
    }
  }, [selectedTemplate]);

  const sendRejectionEmailCallback = useCallback(
    async (laterOption: number) => {
      const templateId = selectedTemplate?.id ?? 0;
      const updateRating = showUpdateRatingField ? shouldUpdateRating : undefined;
      const rejectVia = rejectViaId === 1 && templateId === 0 ? 0 : rejectViaId;

      if (isMandatory && !templateSubject.trim()) {
        errorHandler?.('The rejection email subject CANNOT be empty');
        return;
      }
      if (isMandatory && !templateMessage.trim()) {
        errorHandler?.('The rejection email body CANNOT be empty');
        return;
      }

      loadingHandler?.(true);

      const data: CandidatesRejectionParams = {
        candidateIds: candidateIds,
        subject: templateSubject,
        body: templateMessage,
        templateId: templateId,
        rejectionComments: rejectionNotes,
        rejectionMadeViaId: rejectVia,
        sendDelayTypeId: laterOption,
      };

      let res: boolean | null = null;
      if (candidateIds.length === 1) {
        res = await SendRejectionEmail(candidateIds[0], jobId, data, errorHandler);
      } else if (candidateIds.length > 1) {
        res = await SendRejectionEmailByCandidateList(jobId, data, errorHandler);
      }

      if (res) onRejectionEmailSent(true, updateRating);
      loadingHandler?.(false);
    },
    [
      selectedTemplate,
      isMandatory,
      onRejectionEmailSent,
      candidateIds,
      jobId,
      templateSubject,
      templateMessage,
      rejectionNotes,
      rejectViaId,
      shouldUpdateRating,
      showUpdateRatingField,
      errorHandler,
      loadingHandler,
    ]
  );

  const updateRatingChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;
      setShouldUpdateRating(v === '1');
    },
    []
  );

  const rejectViaChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRejectViaId(+e.target.value);
    },
    []
  );

  const templateSubjectChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTemplateSubject(e.target.value);
    },
    []
  );

  const rejectionNotesChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRejectionNotes(e.target.value);
    },
    []
  );

  const canEditSelectedTemplateContents = useMemo(() => {
    if (canEditCompanyTemplates) return true;
    if (selectedTemplate && selectedTemplate.recruiterID === 0) return false;
    return true;
  }, [selectedTemplate, canEditCompanyTemplates]);

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogTitle>Rejection Email</DialogTitle>
      <DialogContent>
        <DialogContentText mb="10px">{`A Rejection Email ${isMandatory ? 'MUST' : 'should'} be sent. Please choose a template from below`}</DialogContentText>
        <Stack spacing={2}>
          {showUpdateRatingField && (
            <TextField
              select
              label="Update Rating"
              value={shouldUpdateRating ? '1' : '0'}
              onChange={updateRatingChangeHandler}
            >
              <MenuItem value="0">No</MenuItem>
              <MenuItem value="1">Yes</MenuItem>
            </TextField>
          )}
          <TextField
            select
            label="Reject Via"
            value={rejectViaId.toString()}
            onChange={rejectViaChangeHandler}
          >
            {!isMandatory && <MenuItem value="0">None</MenuItem>}
            <MenuItem value="1">Email</MenuItem>
            {!isMandatory && <MenuItem value="2">Phone</MenuItem>}
            {!isMandatory && <MenuItem value="3">Other</MenuItem>}
          </TextField>
          <Box display={rejectViaId === 1 ? undefined : 'none'}>
            <MessageTemplatePicker
              templateId={selectedTemplate?.id ?? 0}
              onSelectHandler={setSelectedTemplate}
              templateSource="Me"
              entityTypeFilter={3}
              typeFilter={3}
              includeCompanyOwned
              appendToStart={specialOptions}
            />
          </Box>
          {rejectViaId === 1 && (selectedTemplate?.id ?? 0) !== 0 && (
            <RWTextFieldComponent
              label="Subject"
              value={templateSubject}
              onChange={templateSubjectChangeHandler}
              disabled={!canEditSelectedTemplateContents}
            />
          )}
          {rejectViaId === 1 && (selectedTemplate?.id ?? 0) !== 0 && (
            <TextAreaWithFormat
              initialValue={templateMessage}
              onContentChange={setTemplateMessage}
              disabled={!canEditSelectedTemplateContents}
            />
          )}
          {(rejectViaId === 2 || rejectViaId === 3) && (
            <RWTextFieldComponent
              label="Notes"
              multiline
              rows={8}
              value={rejectionNotes}
              onChange={rejectionNotesChangeHandler}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="error" onClick={closeHandler}>
          Cancel
        </Button>
        <Box ml="5px">
          <ActionsSplitButton
            mainAction={() => sendRejectionEmailCallback(0)}
            mainActionDisabled={isMandatory && !selectedTemplate?.id}
            secondaryActionDisabled={isMandatory && !selectedTemplate?.id}
            disablePopperPortal={false}
            label="Send"
            color="success"
            menuPlacement="top-end"
          >
            <MenuItem onClick={() => sendRejectionEmailCallback(1)}>In an Hour</MenuItem>
            <MenuItem onClick={() => sendRejectionEmailCallback(2)}>In 4 Hours</MenuItem>
            <MenuItem onClick={() => sendRejectionEmailCallback(3)}>In 8 Hours</MenuItem>
            <MenuItem onClick={() => sendRejectionEmailCallback(4)}>In 12 Hours</MenuItem>
            <MenuItem onClick={() => sendRejectionEmailCallback(5)}>In 24 Hours</MenuItem>
            <MenuItem onClick={() => sendRejectionEmailCallback(6)}>In 2 Days</MenuItem>
            <MenuItem onClick={() => sendRejectionEmailCallback(7)}>In 3 Days</MenuItem>
            <MenuItem onClick={() => sendRejectionEmailCallback(9)}>In 5 Days</MenuItem>
            <MenuItem onClick={() => sendRejectionEmailCallback(8)}>In 1 Week</MenuItem>
          </ActionsSplitButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
